import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Filter, ChevronDown, ChevronUp, ShieldAlert, Banknote, CheckCircle2,
  XCircle, AlertTriangle, RefreshCw, Eye, FileText, Upload,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../lib/api';

const STATUS_LABELS = {
  pending_organiser_review: 'Pending Organiser Review',
  approved_by_organiser:    'Approved by Organiser',
  rejected_by_organiser:    'Rejected by Organiser',
  pending_admin_processing: 'Pending Admin Processing',
  processing_payfast:       'Processing via PayFast',
  refunded_payfast:         'Refunded via PayFast',
  manual_eft_required:      'Manual EFT Required',
  manual_eft_paid:          'Manual EFT Paid',
  failed:                   'Failed',
  cancelled:                'Cancelled',
};

const STATUS_TONE = {
  pending_organiser_review: 'bg-amber-50 text-amber-800 border-amber-200',
  approved_by_organiser:    'bg-blue-50 text-blue-800 border-blue-200',
  rejected_by_organiser:    'bg-rose-50 text-rose-800 border-rose-200',
  pending_admin_processing: 'bg-violet-50 text-violet-800 border-violet-200',
  processing_payfast:       'bg-indigo-50 text-indigo-800 border-indigo-200',
  refunded_payfast:         'bg-emerald-50 text-emerald-800 border-emerald-200',
  manual_eft_required:      'bg-orange-50 text-orange-800 border-orange-200',
  manual_eft_paid:          'bg-emerald-50 text-emerald-800 border-emerald-200',
  failed:                   'bg-rose-50 text-rose-800 border-rose-200',
  cancelled:                'bg-stone-100 text-stone-700 border-stone-200',
};

const Pill = ({ status }) => (
  <span
    data-testid={`refund-status-${status}`}
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium tracking-wide border ${STATUS_TONE[status] || 'bg-stone-100 text-stone-700 border-stone-200'}`}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

const Row = ({ label, value, mono = false }) => (
  <div className="flex justify-between text-xs py-1">
    <span className="text-stone-500">{label}</span>
    <span className={`${mono ? 'font-mono' : ''} text-stone-800 text-right`}>{value || '—'}</span>
  </div>
);

const RefundDetailDialog = ({ refund, onClose, onAction, onRefresh }) => {
  const [revealing, setRevealing] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [confirmReveal, setConfirmReveal] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // { action, label }
  const [notes, setNotes] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  if (!refund) return null;

  const status = refund.status;
  const canProcessPF      = status === 'pending_admin_processing';
  const canMarkRefundedPF = ['pending_admin_processing', 'processing_payfast'].includes(status);
  const canRequireEFT     = ['pending_admin_processing', 'processing_payfast', 'failed'].includes(status);
  const canMarkEFTPaid    = status === 'manual_eft_required' && !!refund.bank_details_summary;
  const canFail           = ['pending_admin_processing', 'processing_payfast', 'manual_eft_required'].includes(status);
  const canCancel         = ['pending_admin_processing', 'processing_payfast', 'manual_eft_required', 'failed'].includes(status);

  const openAction = (action, label) => {
    setActionDialog({ action, label });
    setNotes('');
    setPaymentRef('');
    setPaymentDate(new Date().toISOString().slice(0, 10));
  };

  const submitAction = async () => {
    if (!actionDialog) return;
    setSubmitting(true);
    try {
      const body = { notes: notes || undefined };
      if (actionDialog.action === 'mark-eft-paid') {
        if (!paymentRef.trim()) {
          toast.error('Please enter the bank payment reference.');
          setSubmitting(false);
          return;
        }
        body.manual_payment_reference = paymentRef.trim();
        body.manual_payment_date = paymentDate;
      }
      await onAction(refund.refund_id, actionDialog.action, body);
      setActionDialog(null);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const reveal = async () => {
    setRevealing(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/refund-requests/${refund.refund_id}/bank-details/reveal`,
        {},
        { withCredentials: true },
      );
      setBankDetails(r.data);
      setConfirmReveal(false);
      toast.success('Banking details revealed (audit-logged).');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reveal banking details.');
    } finally {
      setRevealing(false);
    }
  };

  return (
    <>
      <Dialog open={!!refund} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="refund-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1F3D2B]">Refund #{refund.refund_id.slice(0, 8).toUpperCase()}</DialogTitle>
            <DialogDescription>
              <Pill status={status} />
            </DialogDescription>
          </DialogHeader>

          {/* Order + customer + PayFast ID */}
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="bg-stone-50 rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wider text-[#9C7434] mb-2 font-semibold">Customer & order</h3>
              <Row label="Customer" value={refund.customer_name} />
              <Row label="Email" value={refund.customer_email} />
              <Row label="Order" value={(refund.order_id || '').slice(0, 8).toUpperCase()} mono />
              <Row label="Event" value={refund.event_title} />
              <Row label="Requested at" value={(refund.requested_at || '').slice(0, 19).replace('T', ' ')} />
            </div>
            <div className="bg-stone-50 rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wider text-[#9C7434] mb-2 font-semibold">Amounts & PayFast</h3>
              <Row label="Amount paid" value={`R${(refund.amount_paid || 0).toFixed(2)}`} />
              <Row label="Requested refund" value={`R${(refund.refund_amount_requested || 0).toFixed(2)}`} />
              <Row label="Approved refund" value={refund.approved_refund_amount != null ? `R${refund.approved_refund_amount.toFixed(2)}` : '—'} />
              <Row label="Refund method" value={refund.refund_method ? refund.refund_method.replace('_', ' ').toUpperCase() : '—'} />
              <Row label="PayFast payment ID" value={refund.payfast_payment_id || '—'} mono />
            </div>
          </div>

          {/* Reason + notes */}
          <div className="mt-4 bg-amber-50 border-l-4 border-amber-300 rounded p-4">
            <div className="text-xs font-semibold text-amber-900 mb-1 uppercase tracking-wider">Customer reason</div>
            <div className="text-sm text-stone-700">{refund.reason || '—'}</div>
          </div>
          {refund.organiser_notes && (
            <div className="mt-3 bg-blue-50 border-l-4 border-blue-300 rounded p-4">
              <div className="text-xs font-semibold text-blue-900 mb-1 uppercase tracking-wider">Organiser notes</div>
              <div className="text-sm text-stone-700">{refund.organiser_notes}</div>
            </div>
          )}
          {refund.admin_notes && (
            <div className="mt-3 bg-violet-50 border-l-4 border-violet-300 rounded p-4">
              <div className="text-xs font-semibold text-violet-900 mb-1 uppercase tracking-wider">Admin notes</div>
              <div className="text-sm text-stone-700">{refund.admin_notes}</div>
            </div>
          )}

          {/* Banking details (manual EFT) */}
          {(status === 'manual_eft_required' || status === 'manual_eft_paid') && (
            <div className="mt-4 border border-orange-200 bg-orange-50/30 rounded-lg p-4" data-testid="refund-bank-details-block">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#1F3D2B] flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-orange-700" /> Customer banking details
                </h3>
                {refund.bank_details_summary ? (
                  bankDetails ? (
                    <span className="text-xs text-emerald-700 font-medium">Revealed (this view only)</span>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setConfirmReveal(true)}
                      data-testid="refund-reveal-bank-btn"
                      className="text-xs"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> Reveal full details
                    </Button>
                  )
                ) : (
                  <span className="text-xs text-stone-500">Awaiting customer submission</span>
                )}
              </div>

              {refund.bank_details_summary ? (
                <div className="space-y-1 text-sm">
                  <Row label="Account holder" value={bankDetails?.account_holder_name || refund.bank_details_summary.account_holder_name} />
                  <Row label="Bank" value={bankDetails?.bank_name || refund.bank_details_summary.bank_name} />
                  <Row label="Branch code" value={bankDetails?.branch_code || refund.bank_details_summary.branch_code} mono />
                  <Row label="Account type" value={(bankDetails?.account_type || refund.bank_details_summary.account_type || '—').toUpperCase()} />
                  <Row
                    label="Account number"
                    value={bankDetails?.account_number || refund.bank_details_summary.account_number_masked}
                    mono
                  />
                  <Row label="Consent confirmed" value={refund.bank_details_summary.consent_confirmed ? 'Yes' : 'No'} />
                  <Row label="Submitted at" value={(refund.bank_details_summary.submitted_at || '').slice(0, 19).replace('T', ' ')} />
                </div>
              ) : (
                <p className="text-xs text-stone-600 mt-2">
                  Customer will be notified via email and asked to submit their bank details via a secure VinePass page. They will appear here once submitted.
                </p>
              )}
            </div>
          )}

          {refund.manual_payment_reference && (
            <div className="mt-4 bg-emerald-50 border-l-4 border-emerald-400 rounded p-4">
              <div className="text-xs font-semibold text-emerald-900 mb-1 uppercase tracking-wider">EFT payment</div>
              <Row label="Reference" value={refund.manual_payment_reference} mono />
              <Row label="Paid on" value={(refund.manual_payment_date || '').slice(0, 10)} />
              {refund.proof_of_payment_url && (
                <a
                  href={refund.proof_of_payment_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center text-xs text-emerald-800 underline mt-1"
                >
                  <FileText className="w-3 h-3 mr-1" /> Proof of payment
                </a>
              )}
            </div>
          )}

          {/* Admin actions */}
          <div className="mt-6 border-t border-stone-200 pt-4">
            <h3 className="text-xs uppercase tracking-wider text-stone-500 mb-3 font-semibold">Admin actions</h3>
            <div className="flex flex-wrap gap-2">
              {canProcessPF && (
                <Button size="sm" onClick={() => openAction('process-payfast', 'Mark as Processing via PayFast')} data-testid="refund-action-process-payfast" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Processing via PayFast
                </Button>
              )}
              {canMarkRefundedPF && (
                <Button size="sm" onClick={() => openAction('mark-refunded-payfast', 'Mark as Refunded via PayFast')} data-testid="refund-action-mark-refunded-payfast" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Refunded via PayFast
                </Button>
              )}
              {canRequireEFT && (
                <Button size="sm" onClick={() => openAction('require-manual-eft', 'Require Manual EFT')} data-testid="refund-action-require-eft" variant="outline" className="border-orange-300 text-orange-700 text-xs">
                  <Banknote className="w-3.5 h-3.5 mr-1.5" /> Manual EFT Required
                </Button>
              )}
              {canMarkEFTPaid && (
                <Button size="sm" onClick={() => openAction('mark-eft-paid', 'Mark Manual EFT Paid')} data-testid="refund-action-mark-eft-paid" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Manual EFT Paid
                </Button>
              )}
              {canFail && (
                <Button size="sm" onClick={() => openAction('mark-failed', 'Mark as Failed')} data-testid="refund-action-mark-failed" variant="outline" className="border-rose-300 text-rose-700 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Failed
                </Button>
              )}
              {canCancel && (
                <Button size="sm" onClick={() => openAction('cancel', 'Cancel Refund')} data-testid="refund-action-cancel" variant="outline" className="text-stone-700 text-xs">
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
                </Button>
              )}
            </div>
          </div>

          {/* Audit log */}
          {Array.isArray(refund.audit_log) && refund.audit_log.length > 0 && (
            <div className="mt-4 bg-stone-50 rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-semibold">Audit log</h3>
              <ul className="text-xs text-stone-600 space-y-1">
                {refund.audit_log.map((a, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span>{a.action}{a.to_status ? ` → ${a.to_status}` : ''}</span>
                    <span className="text-stone-400 font-mono">{(a.at || '').slice(0, 19).replace('T', ' ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reveal confirmation */}
      <Dialog open={confirmReveal} onOpenChange={setConfirmReveal}>
        <DialogContent data-testid="refund-reveal-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> Confirm full banking detail reveal
            </DialogTitle>
            <DialogDescription>
              You are about to reveal the customer's full account number. This action is audit-logged and visible to other admins. Only proceed if you are processing this refund right now.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setConfirmReveal(false)}>Cancel</Button>
            <Button onClick={reveal} disabled={revealing} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="refund-reveal-confirm-btn">
              {revealing ? 'Revealing…' : 'I understand — reveal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generic action dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent data-testid="refund-action-dialog">
          <DialogHeader>
            <DialogTitle>{actionDialog?.label}</DialogTitle>
            <DialogDescription>
              The customer{actionDialog?.action !== 'cancel' && actionDialog?.action !== 'mark-failed' ? ', organiser' : ''} will be notified by email where applicable.
            </DialogDescription>
          </DialogHeader>
          {actionDialog?.action === 'mark-eft-paid' && (
            <div className="space-y-3 mt-2">
              <div>
                <Label htmlFor="ref">Bank payment reference *</Label>
                <Input id="ref" data-testid="refund-payment-reference" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="EFT12345 / Standard Bank ref" />
              </div>
              <div>
                <Label htmlFor="dt">Payment date</Label>
                <Input id="dt" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
              </div>
            </div>
          )}
          <div className="mt-3">
            <Label htmlFor="notes">Internal notes (optional)</Label>
            <Textarea id="notes" data-testid="refund-action-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000} />
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={submitAction} disabled={submitting} className="bg-[#1F3D2B] hover:bg-[#1F3D2B]/90 text-white" data-testid="refund-action-submit">
              {submitting ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AdminRefundsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // Filters
  const [fStatus, setFStatus] = useState('all');
  const [fEmail, setFEmail] = useState('');
  const [fEvent, setFEvent] = useState('');
  const [fMethod, setFMethod] = useState('all');
  const [fMin, setFMin] = useState('');
  const [fMax, setFMax] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin only.');
      navigate('/');
    }
  }, [user, navigate]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fStatus !== 'all') params.status = fStatus;
      if (fEmail.trim()) params.customer_email = fEmail.trim().toLowerCase();
      if (fEvent.trim()) params.event_q = fEvent.trim();
      if (fMethod !== 'all') params.refund_method = fMethod;
      if (fMin) params.min_amount = parseFloat(fMin);
      if (fMax) params.max_amount = parseFloat(fMax);

      const r = await axios.get(`${BACKEND_URL}/api/admin/refund-requests`, {
        withCredentials: true,
        params,
      });
      setRows(r.data.refund_requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load refunds.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-open the focused refund (deep link from admin notification email)
  useEffect(() => {
    if (focusId && rows.length) {
      const r = rows.find((x) => x.refund_id === focusId);
      if (r) openDetail(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, rows]);

  const openDetail = async (r) => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/admin/refund-requests/${r.refund_id}`, { withCredentials: true });
      setSelected(resp.data.refund_request);
    } catch (e) {
      toast.error('Could not load refund.');
    }
  };

  const onAction = async (refund_id, action, body) => {
    const r = await axios.post(
      `${BACKEND_URL}/api/admin/refund-requests/${refund_id}/${action}`,
      body || {},
      { withCredentials: true },
    );
    toast.success(`Status updated → ${STATUS_LABELS[r.data.status] || r.data.status}`);
    setSelected(r.data.refund_request);
    fetchRows();
  };

  const filteredCount = rows.length;

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1F3D2B]">Refund Requests</h1>
            <p className="text-sm text-stone-600 mt-1">
              Process via PayFast first; switch to manual EFT only when required.
            </p>
          </div>
          <Button variant="outline" onClick={fetchRows} data-testid="refund-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-stone-200 p-4 mb-6">
          <div className="flex items-center gap-2 text-xs text-stone-600 mb-3">
            <Filter className="w-3.5 h-3.5" />
            <span className="font-medium uppercase tracking-wider">Filters</span>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label className="text-xs">Event name contains</Label>
              <Input
                data-testid="filter-event"
                value={fEvent}
                onChange={(e) => setFEvent(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchRows(); }}
                placeholder="e.g. Stellenbosch Tasting"
              />
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={fMethod} onValueChange={setFMethod}>
                <SelectTrigger data-testid="filter-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="payfast">PayFast</SelectItem>
                  <SelectItem value="manual_eft">Manual EFT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Customer email</Label>
              <Input data-testid="filter-email" value={fEmail} onChange={(e) => setFEmail(e.target.value)} placeholder="name@example.com" />
            </div>
            <div>
              <Label className="text-xs">Min amount</Label>
              <Input data-testid="filter-min" value={fMin} onChange={(e) => setFMin(e.target.value)} type="number" placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Max amount</Label>
              <Input data-testid="filter-max" value={fMax} onChange={(e) => setFMax(e.target.value)} type="number" placeholder="9999" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={fetchRows} className="bg-[#1F3D2B] hover:bg-[#1F3D2B]/90" data-testid="filter-apply-btn">
              Apply filters
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-stone-500">Loading refunds…</div>
        ) : filteredCount === 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 p-12 text-center" data-testid="refund-empty-state">
            <ShieldAlert className="w-10 h-10 text-stone-400 mx-auto mb-3" />
            <p className="text-stone-600">No refund requests match these filters.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-left">
                  <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Customer</th>
                  <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden md:table-cell">Event</th>
                  <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Amount</th>
                  <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
                  <th className="p-3 text-xs font-semibold text-stone-600 uppercase tracking-wider hidden lg:table-cell">Requested</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.refund_id} className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`refund-row-${r.refund_id.slice(0, 8)}`}>
                    <td className="p-3">
                      <div className="font-medium text-stone-800">{r.customer_name || '—'}</div>
                      <div className="text-xs text-stone-500">{r.customer_email}</div>
                    </td>
                    <td className="p-3 hidden md:table-cell text-stone-700">{r.event_title || '—'}</td>
                    <td className="p-3 font-semibold text-[#1F3D2B]">R{(r.amount || r.refund_amount_requested || 0).toFixed(2)}</td>
                    <td className="p-3"><Pill status={r.status} /></td>
                    <td className="p-3 hidden lg:table-cell text-xs text-stone-500">{(r.requested_at || '').slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`refund-open-${r.refund_id.slice(0, 8)}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RefundDetailDialog
        refund={selected}
        onClose={() => setSelected(null)}
        onAction={onAction}
        onRefresh={fetchRows}
      />
    </div>
  );
};

export default AdminRefundsPage;
